import { buildCancelLicenseSubscriptionBody } from "@/components/billing/cancel-license-subscription-body";
import { ButtonLoading } from "@/components/button-loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import { useLicenseErrorToast } from "@/hooks/use-license-error-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDateNumeric } from "@/lib/format-amount";
import { cn } from "@/lib/utils";
import {
  LICENSE_CANCELLATION_COMMENT_MAX_LENGTH,
  LICENSE_CANCELLATION_REASONS,
  LicenseCancellationReason,
  LicenseFrequencyDTO,
  TeamLicenseInventoryDTO,
} from "@shaarp/common";
import { AlertTriangleIcon } from "lucide-react";
import { useState } from "react";

type DialogLicenseCancelConfirmProps = {
  frequency: "monthly" | "annual";
  freqData: LicenseFrequencyDTO;
  onClose: () => void;
  onConfirmed: (updatedInventory: TeamLicenseInventoryDTO) => void;
};

/**
 * Confirmation dialog for cancelling an entire license subscription (monthly or annual).
 * Offers an optional reason and comment; the checkbox acknowledgement stays the
 * only condition to submit.
 */
export const DialogLicenseCancelConfirm = ({
  frequency,
  freqData,
  onClose,
  onConfirmed,
}: DialogLicenseCancelConfirmProps) => {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const { toast } = useToast();
  const showLicenseError = useLicenseErrorToast();
  const idempotencyKey = useIdempotencyKey();

  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState<LicenseCancellationReason | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDate = (date: Date | null | undefined) =>
    formatDateNumeric(date, i18n.language);

  const frequencyLabel =
    frequency === "monthly"
      ? t("team.licenseManagement.monthly")
      : t("team.licenseManagement.annual");

  // The API refuses a longer comment rather than cutting it, so the textarea
  // lets the manager write — or paste — past the limit and says what to fix.
  // `maxLength` would silently drop the end of a pasted paragraph.
  const isCommentTooLong =
    comment.trim().length > LICENSE_CANCELLATION_COMMENT_MAX_LENGTH;

  const handleConfirm = async () => {
    if (!confirmed || isCommentTooLong) return;
    try {
      setIsSubmitting(true);
      const { data } = await api.post<TeamLicenseInventoryDTO>(
        "/my/team/licenses/cancel",
        buildCancelLicenseSubscriptionBody({ frequency, reason, comment }),
        { headers: { "Idempotency-Key": idempotencyKey } }
      );
      toast({
        variant: "default",
        title: t("team.licenseManagement.title"),
        description: t("team.licenseManagement.scheduledCancel", {
          date: formatDate(freqData.currentPeriodEndsAt),
        }),
      });
      onConfirmed(data);
    } catch (error) {
      showLicenseError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // Closing mid-request would leave the cancellation running with nobody to
    // read its answer, and the remount would issue a new idempotency key.
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <DialogContent className="max-w-[520px] w-full max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t("team.licenseManagement.cancelDialog.title", {
              frequency: frequencyLabel,
            })}
          </DialogTitle>
        </DialogHeader>

        <Separator />

        {/*
          Outside the scroll area: the acknowledgement below attests this
          warning, so it must still be on screen when the box is ticked.
        */}
        <Alert variant="destructive">
          <AlertTriangleIcon className="w-4 h-4" />
          <AlertDescription>
            {t("team.licenseManagement.cancelDialog.warning", {
              date: formatDate(freqData.currentPeriodEndsAt),
            })}
          </AlertDescription>
        </Alert>

        {/*
          Only the optional feedback scrolls on a short screen: the title, the
          warning, the acknowledgement — the one condition to submit — and the
          buttons stay in view.
        */}
        <div className="space-y-4 overflow-y-auto min-h-0 -mx-1 px-1">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 min-h-7">
              <Label id="license-cancel-reason-label">
                {t("team.licenseManagement.cancelDialog.reasonLabel")}
              </Label>
              {/* A radio group cannot be unselected by clicking again. */}
              {reason && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground"
                  onClick={() => setReason(null)}
                >
                  {t("team.licenseManagement.cancelDialog.clearReason")}
                </Button>
              )}
            </div>
            <RadioGroup
              aria-labelledby="license-cancel-reason-label"
              className="gap-1.5"
              value={reason ?? ""}
              onValueChange={(value) =>
                setReason(
                  LICENSE_CANCELLATION_REASONS.find((code) => code === value) ??
                    null
                )
              }
            >
              {LICENSE_CANCELLATION_REASONS.map((code) => (
                <label
                  key={code}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <RadioGroupItem value={code} />
                  {t(`team.licenseManagement.cancelDialog.reasons.${code}`)}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="license-cancel-comment">
              {t("team.licenseManagement.cancelDialog.commentLabel")}
            </Label>
            <Textarea
              id="license-cancel-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              aria-invalid={isCommentTooLong}
              aria-describedby="license-cancel-comment-count"
              placeholder={t(
                "team.licenseManagement.cancelDialog.commentPlaceholder"
              )}
              className="min-h-[80px] resize-none"
            />
            <p
              id="license-cancel-comment-count"
              className={cn(
                "text-xs text-right",
                isCommentTooLong ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {isCommentTooLong &&
                `${t("team.licenseManagement.cancelDialog.commentTooLong", {
                  max: LICENSE_CANCELLATION_COMMENT_MAX_LENGTH,
                })} `}
              {comment.trim().length}/{LICENSE_CANCELLATION_COMMENT_MAX_LENGTH}
            </p>
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(Boolean(checked))}
            className="mt-0.5"
          />
          <span className="text-sm">
            {t("team.licenseManagement.cancelDialog.confirmCheckbox")}
          </span>
        </label>

        <Separator />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t("team.licenseManagement.cancelDialog.back")}
          </Button>
          <ButtonLoading
            variant="destructive"
            isLoading={isSubmitting}
            disabled={!confirmed || isCommentTooLong || isSubmitting}
            onClick={handleConfirm}
          >
            {t("team.licenseManagement.cancelDialog.confirm")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
